import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaLightbulb, FaToggleOn, FaPlug, FaThermometerHalf, FaVideo, FaTv,
  FaSnowflake, FaVolumeUp, FaBlender, FaLock, FaWindowMaximize, FaEllipsisH,
} from 'react-icons/fa';

/**
 * E1 — "Add Device" category foundation (instruction §13).
 *
 * Deliberately does NOT reimplement the mobile app's discovery flow
 * (QR scanning, local network mDNS/SSDP scanning, BLE commissioning) —
 * those require native device capabilities a browser page genuinely
 * doesn't have, and faking a "working" discovery UI that can't actually
 * discover anything from a browser would be dishonest. This page is a
 * real, useful category browser grounded in the canonical capability
 * model (backend/capabilities.py) and points users to the client that
 * can actually complete pairing for each category.
 */

const CATEGORIES = [
  { icon: FaLightbulb, label: 'Lights', hint: 'Zigbee, Matter, Wi-Fi, Tuya' },
  { icon: FaToggleOn, label: 'Switches', hint: 'Zigbee, Matter, Wi-Fi, Tuya' },
  { icon: FaPlug, label: 'Plugs', hint: 'Zigbee, Matter, Wi-Fi, Tuya' },
  { icon: FaThermometerHalf, label: 'Sensors', hint: 'Zigbee, Matter' },
  { icon: FaVideo, label: 'Cameras', hint: 'ONVIF, RTSP, cloud camera brands' },
  { icon: FaTv, label: 'TVs', hint: 'Roku, Chromecast, Fire TV, Samsung, LG, Apple TV' },
  { icon: FaSnowflake, label: 'AC', hint: 'IR (Broadlink) + network-controlled units' },
  { icon: FaVolumeUp, label: 'Audio', hint: 'Network + IR-controlled systems' },
  { icon: FaBlender, label: 'Appliances', hint: 'Protocol-dependent — growing support' },
  { icon: FaLock, label: 'Locks', hint: 'Zigbee, Matter' },
  { icon: FaWindowMaximize, label: 'Curtains', hint: 'Zigbee, Matter' },
  { icon: FaEllipsisH, label: 'Other', hint: 'See Compatible Devices' },
];

export default function AddDevice() {
  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Add Device</h1>
      <p className="text-white/50 mb-8 max-w-2xl">
        Sheba supports devices across these categories through its gateway and
        provider architecture. Pairing a new device (scanning a QR code,
        discovering it on your local network, or commissioning it over
        Bluetooth) requires the mobile app or customer portal, which have
        access to your camera, local network, and Bluetooth — a website page
        cannot complete these steps on its own. Browse a category below, then
        continue pairing from the app.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {CATEGORIES.map(({ icon: Icon, label, hint }) => (
          <div key={label} className="glass rounded-xl p-4 text-center">
            <Icon className="text-2xl text-[#00d4ff] mx-auto mb-2" />
            <p className="font-medium text-sm">{label}</p>
            <p className="text-[11px] text-white/40 mt-1">{hint}</p>
          </div>
        ))}
      </div>

      <div className="glass rounded-xl p-5">
        <p className="text-sm text-white/70">
          Already have the Sheba mobile app? Open it and go to{' '}
          <strong>Add Device</strong> to pair a new device — it will appear
          here and in the app automatically, since both use the same account.
        </p>
      </div>

      <Link to="/dashboard/devices" className="inline-block mt-6 text-sm text-white/50 hover:text-white">
        ← Back to My Devices
      </Link>
    </div>
  );
}
